import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { useSectionHeadSlot } from '../cms/SectionShell';
import { useReachDecisions } from '../cms/todayData';
import { AgentFirstButton } from '../cms/AskYourAgent';
import { showInkToast } from '../cms/InkToast';
import { ChannelLogo } from './ChannelLogo';
import { PostPanel, TONE } from './PostPanel';
import { usePostActions } from './postActions';
import { WritePost } from './WritePost';
import { compact, PostVisits, REACH_POST_VISITS } from './reachVisits';
import {
  Channel,
  channelLabel,
  channelName,
  goneStatus,
  postText,
  REACH_CHANNELS,
  REACH_POSTS,
} from './reach';
import {
  disconnectedChannels,
  footerWords,
  linkWords,
  ListedPost,
  PostFilters,
  readPostFilters,
  sourceOptions,
  sourceWords,
  statusReason,
  STATUSES,
  whenWords,
  writePostFilters,
} from './postListWords';

/**
 * Social › Posts (designer notes "Website › Pages: editing, and Social ›
 * Posts", 2026-10-28, backlog 4e7c78c8): every post on the site — past,
 * upcoming, didn't post, skipped — and the drafts with no date, in one table.
 * Channel, status, source, words and sort all combine; each filter's counts
 * come from the server with the other filters applied (reachPosts). A row
 * opens the post panel, as on the Calendar, or the composer for a draft.
 * The filters live in the address (ch, st, src, q, sort), so a reload, Back or
 * a shared link keeps the view (f593b133).
 *
 * Deleted is one of the statuses, so each channel's chip shows its own: what
 * was deleted stays listed, to read, until someone presses Permanently delete.
 */

interface PostList {
  posts: ListedPost[];
  total: number;
  nextCursor: string | null;
  counts: {
    status: Record<string, number>;
    channel: Record<string, number>;
    source?: Record<string, number>;
  };
  sources: Array<{ id: string; name: string }>;
}

const SEARCH_WAIT_MS = 300;

export function ReachPosts() {
  const { accountId } = useAccount();
  const headSlot = useSectionHeadSlot();
  const decisions = useReachDecisions();

  const [params, setParams] = useSearchParams();
  const { channel, status, source, q, sort } = readPostFilters(params);
  // Picking a filter is a step Back undoes; typing only replaces the address.
  const setFilters = (patch: Partial<PostFilters>, replace = false) =>
    setParams((cur) => writePostFilters(cur, { ...readPostFilters(cur), ...patch }), { replace });
  const [search, setSearch] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) setFilters({ q: search }, true);
    }, SEARCH_WAIT_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  // Back, or a link, changed the words: show them in the box.
  useEffect(() => {
    setSearch((typed) => (typed.trim() === q ? typed : q));
  }, [q]);
  const filtered = Boolean(channel || status || source || q);
  const clear = () => {
    setSearch('');
    setFilters({ channel: null, status: null, source: '', q: '' });
  };

  const variables = {
    accountId,
    channel,
    status,
    source: source || null,
    q: q || null,
    sort,
  };
  const { data, previousData, loading, error, refetch, fetchMore } = useQuery<{
    reachPosts: PostList;
  }>(REACH_POSTS, {
    variables,
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  // Keep the last list on screen while the next filter loads.
  const list = (data || previousData)?.reachPosts;
  const posts = useMemo(() => list?.posts || [], [list]);
  const [more, setMore] = useState(false);
  const loadMore = async () => {
    if (!list?.nextCursor) return;
    setMore(true);
    try {
      await fetchMore({
        variables: { ...variables, cursor: list.nextCursor },
        updateQuery: (prev, { fetchMoreResult }) =>
          fetchMoreResult
            ? {
                reachPosts: {
                  ...fetchMoreResult.reachPosts,
                  posts: [...prev.reachPosts.posts, ...fetchMoreResult.reachPosts.posts],
                },
              }
            : prev,
      });
    } finally {
      setMore(false);
    }
  };

  const { data: channelData } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
  });
  const allChannels = channelData?.socialProfiles || [];
  const connected = allChannels.filter((c) => c.isActive);
  const channelById = new Map(allChannels.map((c) => [c.id, c]));

  // Results: clicks to the site, then views and reactions, for posted rows.
  const postedIds = useMemo(
    () => posts.filter((p) => p.reachStatus === 'posted' && !p.deletionStatus).map((p) => p.id),
    [posts]
  );
  const { data: visitData } = useQuery<{ reachPostVisits: PostVisits }>(REACH_POST_VISITS, {
    variables: { accountId, postIds: postedIds.slice(0, 200) },
    skip: !accountId || !postedIds.length,
    fetchPolicy: 'cache-and-network',
  });
  const visits = visitData?.reachPostVisits;
  const clicksLive = Boolean(visits?.connected && visits.syncedAt);
  const visitsOf = new Map((visits?.posts || []).map((v) => [v.postId, v]));

  const [openId, setOpenId] = useState<string | null>(null);
  const [writing, setWriting] = useState<ListedPost | 'new' | null>(null);
  const openPost = posts.find((p) => p.id === openId) || null;
  const reload = () => Promise.all([refetch(), decisions.reach.refetch()]);
  const { busy, act, remove, erase } = usePostActions({
    onDone: () => {
      setOpenId(null);
      return reload();
    },
  });
  const open = (p: ListedPost) => {
    if (p.deletionStatus) {
      setOpenId(p.id);
      return;
    }
    // A disconnected channel's posts can't be tried again or sent from here.
    if (channelById.get(p.socialProfileId)?.isActive === false && p.reachStatus !== 'posted') {
      showInkToast(
        `Reconnect ${channelName(p.platform)} in Social → Channels to retry or delete these posts.`
      );
      return;
    }
    if (p.reachStatus === 'draft') setWriting(p);
    else setOpenId(p.id);
  };

  const statusCounts = list?.counts.status || {};
  const channelCounts = list?.counts.channel || {};
  const disconnected = disconnectedChannels(allChannels, channelCounts);
  const shownStatuses = STATUSES.filter((s) => statusCounts[s.id] || status === s.id);
  const count = (n?: number) => (list ? (n || 0).toLocaleString() : '');

  const write = connected.length ? (
    <div className="ink-section-right">
      <AgentFirstButton action="post" label="Write a post" onSelf={() => setWriting('new')} />
    </div>
  ) : null;

  return (
    <div className="rp">
      {headSlot ? createPortal(write, headSlot) : write}

      <div className="rp-filters">
        <div className="rp-chips" role="group" aria-label="Channel">
          <button
            type="button"
            className="ink-chip"
            data-active={!channel ? 'true' : 'false'}
            aria-pressed={!channel}
            onClick={() => setFilters({ channel: null })}
          >
            All channels <span>{count(channelCounts.all)}</span>
          </button>
          {connected.map((c) => (
            <button
              key={c.id}
              type="button"
              className="ink-chip rp-chip"
              data-active={channel === c.id ? 'true' : 'false'}
              aria-pressed={channel === c.id}
              onClick={() => setFilters({ channel: channel === c.id ? null : c.id })}
            >
              <ChannelLogo platform={c.platform} className="rc-mark rc-mark-sm" />
              {channelLabel(c, connected)} <span>{count(channelCounts[c.id])}</span>
            </button>
          ))}
          {disconnected.map((c) => (
            <button
              key={c.id}
              type="button"
              className="ink-chip rp-chip"
              data-disconnected="true"
              data-active={channel === c.id ? 'true' : 'false'}
              aria-pressed={channel === c.id}
              onClick={() => setFilters({ channel: channel === c.id ? null : c.id })}
            >
              <ChannelLogo platform={c.platform} className="rc-mark rc-mark-sm" />
              {channelLabel(c, allChannels)} · disconnected{' '}
              <span>{count(channelCounts[c.id])}</span>
            </button>
          ))}
        </div>

        <div className="rp-row">
          <div className="ink-seg ink-seg-counts rp-status" role="group" aria-label="Status">
            <button
              type="button"
              data-active={!status ? 'true' : 'false'}
              aria-pressed={!status}
              onClick={() => setFilters({ status: null })}
            >
              All <span className="ink-seg-count">{count(statusCounts.all)}</span>
            </button>
            {shownStatuses.map((s) => (
              <button
                key={s.id}
                type="button"
                data-active={status === s.id ? 'true' : 'false'}
                aria-pressed={status === s.id}
                onClick={() => setFilters({ status: s.id })}
              >
                {s.label} <span className="ink-seg-count">{count(statusCounts[s.id])}</span>
              </button>
            ))}
          </div>
          <select
            className="ink-site-input rp-select"
            aria-label="Source"
            value={source}
            onChange={(e) => setFilters({ source: e.target.value })}
          >
            <option value="">All sources</option>
            {sourceOptions(list?.counts.source, list?.sources || [], source).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            className="ink-search rp-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search posts and linked articles"
            aria-label="Search posts and linked articles"
          />
          <select
            className="ink-site-input rp-select"
            aria-label="Sort"
            value={sort}
            onChange={(e) =>
              setFilters({ sort: e.target.value === 'oldest' ? 'oldest' : 'newest' })
            }
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>
      </div>

      {error && !list ? (
        <div className="rp-empty">
          <p>Your posts didn’t load. {error.message}</p>
          <button type="button" className="ink-btn" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      ) : !list ? (
        <div className="rp-table-wrap" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="rp-skel">
              <span className="ink-skel-bar" />
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="rp-empty">
          {filtered ? (
            <>
              <p>No posts match these filters.</p>
              <button type="button" className="ink-btn" onClick={clear}>
                Clear filters
              </button>
            </>
          ) : (
            <p>
              No posts yet. What you and your agent write for your channels will be listed here.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="rp-table-wrap" data-loading={loading ? 'true' : undefined}>
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Post</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>When</th>
                  <th>{status === 'deleted' ? '' : 'Results'}</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => {
                  const gone = goneStatus(p);
                  const tone = TONE[gone || p.reachStatus] || TONE.scheduled;
                  const when = whenWords(p);
                  const ch = channelById.get(p.socialProfileId);
                  const off = ch?.isActive === false;
                  const reason = statusReason(p, ch?.isActive);
                  const v = visitsOf.get(p.id);
                  return (
                    <tr
                      key={p.id}
                      tabIndex={0}
                      onClick={() => open(p)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') open(p);
                      }}
                    >
                      <td>
                        <span
                          className="rp-channel"
                          data-off={off ? 'true' : undefined}
                          title={off ? `${channelName(p.platform)} (disconnected)` : undefined}
                        >
                          <ChannelLogo platform={p.platform} className="rc-mark rc-mark-sm" />
                          <span className="rp-handle">
                            {p.channelHandle || ch?.profileName || channelName(p.platform)}
                          </span>
                        </span>
                      </td>
                      <td className="rp-post">
                        <span className="rp-text">{postText(p) || 'No words yet'}</span>
                        <span className="rp-link" data-none={p.postUrl ? undefined : 'true'}>
                          {linkWords(p)}
                        </span>
                      </td>
                      <td className="rp-source">{sourceWords(p)}</td>
                      <td>
                        <span className="rp-status-cell" style={{ color: tone.ink }}>
                          <span className="ink-dot" style={{ background: tone.dot }} />
                          {tone.label}
                        </span>
                        {reason ? <span className="rp-reason">{reason}</span> : null}
                        {p.voiceCheck?.offVoice ? (
                          <span className="rp-off-voice">
                            <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
                            Off voice
                          </span>
                        ) : null}
                      </td>
                      <td className="rp-when">
                        <span>{when.day}</span>
                        <span className="ink-muted">{when.at}</span>
                      </td>
                      <td className="rp-results">
                        {gone === 'deleted' ? (
                          <button
                            type="button"
                            className="ink-btn"
                            disabled={busy === p.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void erase(p);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            Permanently delete
                          </button>
                        ) : !gone && p.reachStatus === 'posted' ? (
                          <Results v={v} clicks={clicksLive} />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="rp-foot">
            <span className="ink-muted">{footerWords(posts.length, list.total)}</span>
            {list.nextCursor ? (
              <button
                type="button"
                className="ink-btn"
                disabled={more}
                onClick={() => void loadMore()}
              >
                {more ? 'Loading…' : 'Show more'}
              </button>
            ) : null}
          </div>
        </>
      )}

      {openPost ? (
        <PostPanel
          post={openPost}
          busy={busy === openPost.id}
          onClose={() => setOpenId(null)}
          onAct={(kind, at, reason) => void act(openPost, kind, at, reason)}
          onDelete={() => void remove(openPost)}
          onSaved={() => {
            setOpenId(null);
            return reload();
          }}
        />
      ) : null}
      {writing ? (
        <WritePost
          draft={writing === 'new' ? null : writing}
          onClose={() => setWriting(null)}
          onPosted={() => {
            setWriting(null);
            void reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** Clicks, then views and reactions: "42 clicks" over "1.2K views · 8 reactions". */
function Results({
  v,
  clicks,
}: {
  v?: { visits: number; views: number | null; engagements?: number | null };
  clicks: boolean;
}) {
  const n = v?.visits || 0;
  const views = v?.views;
  const reactions = v?.engagements;
  const second = [
    views != null ? `${compact(views)} ${views === 1 ? 'view' : 'views'}` : null,
    reactions != null
      ? `${compact(reactions)} ${reactions === 1 ? 'reaction' : 'reactions'}`
      : null,
  ].filter(Boolean);
  if (!clicks && !second.length) return <>—</>;
  return (
    <span className="rp-results-cell">
      {clicks ? <span>{`${n.toLocaleString()} ${n === 1 ? 'click' : 'clicks'}`}</span> : null}
      {second.length ? <span className="ink-muted">{second.join(' · ')}</span> : null}
    </span>
  );
}
